import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useFlowOwner } from "@/components/FlowAccessGate";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowLeft, Loader2, Mail } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { getGeoData } from "@/utils/geolocation";
import { toast } from "sonner";

type Flow = {
  id: string;
  business_name: string;
  product_name: string;
  logo_url: string | null;
  product_image_url: string | null;
  flow_type: string;
  created_at: string;
  updated_at: string;
  user_id: string;
  price?: number;
  price_period?: string;
  redirect_url?: string;
  thank_you_message?: string;
  newsletter_heading?: string;
  newsletter_subtitle?: string;
  newsletter_cta_text?: string;
  newsletter_color?: string;
};

export default function NewsletterFlow() {
  const { flowId } = useParams();
  const navigate = useNavigate();
  const { isPaid } = useFlowOwner();

  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [flow, setFlow] = useState<Flow | null>(null);
  const [email, setEmail] = useState("");

  useEffect(() => {
    const fetchFlow = async () => {
      try {
        if (!flowId) return;
        
        const { data, error } = await supabase
          .from('flows')
          .select('*')
          .eq('id', flowId)
          .single();
        
        if (error) {
          throw error;
        }
        
        setFlow(data);
        recordFlowVisit();
      } catch (error) {
        console.error('Error fetching flow:', error);
        toast.error('Failed to load flow data');
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchFlow();
  }, [flowId]);

  const recordFlowVisit = async () => {
    if (!flowId) return;
    
    try {
      // Generate a unique key for this flow view
      const viewKey = `flow_view_${flowId}`;
      
      // Check if this user has already viewed this flow today
      const viewDate = new Date().toISOString().split('T')[0]; // Get current date in YYYY-MM-DD format
      const storedView = localStorage.getItem(viewKey);
      
      // If user already viewed this flow today, don't record again
      if (storedView === viewDate) {
        return;
      }
      
      // Store the view in localStorage to prevent duplicates
      localStorage.setItem(viewKey, viewDate);
      
      // Generate a unique identifier for the user if not exists
      let userIdentifier = localStorage.getItem('flow_user_id');
      if (!userIdentifier) {
        userIdentifier = crypto.randomUUID();
        localStorage.setItem('flow_user_id', userIdentifier);
      }
      
      const geoData = await getGeoData();
      
      await supabase.from('flow_analytics').insert({
        flow_id: flowId,
        event_type: 'view',
        referrer: document.referrer || 'direct',
        is_embedded: window.self !== window.top,
        user_agent: navigator.userAgent,
        path: window.location.pathname,
        country: geoData?.country || 'Unknown',
        user_identifier: userIdentifier,
        view_date: viewDate
      });
    } catch (error) {
      console.error('Failed to record view analytics:', error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      if (!email.trim()) {
        throw new Error('Please enter your email address');
      }

      const geoData = await getGeoData();
      
      // Generate a unique identifier for the user if not exists
      let userIdentifier = localStorage.getItem('flow_user_id');
      if (!userIdentifier) {
        userIdentifier = crypto.randomUUID();
        localStorage.setItem('flow_user_id', userIdentifier);
      }
      
      // Get current date in YYYY-MM-DD format
      const viewDate = new Date().toISOString().split('T')[0];
      
      await supabase.from('flow_analytics').insert({
        flow_id: flowId,
        email: email,
        event_type: 'conversion',
        referrer: document.referrer || 'direct',
        is_embedded: window.self !== window.top,
        user_agent: navigator.userAgent,
        path: window.location.pathname,
        country: geoData?.country || 'Unknown',
        user_identifier: userIdentifier,
        view_date: viewDate
      });

      navigate(`/thank-you/${flowId}`);
    } catch (error) {
      console.error('Submission error:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to submit');
      setIsSubmitting(false);
    }
  };

  const getFrequencyText = () => {
    switch (flow?.flow_type) {
      case 'newsletter_daily':
        return 'daily updates';
      case 'newsletter_weekly':
        return 'weekly roundup';
      case 'newsletter_monthly':
        return 'monthly digest';
      default:
        return 'regular updates';
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (!flow) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="text-center">
          <h1 className="text-2xl font-bold">Flow not found</h1>
          <p className="text-muted-foreground">The requested flow could not be found.</p>
        </div>
      </div>
    );
  }

  const accent = flow.newsletter_color || "#2563eb";
  const isLight = (() => {
    const num = parseInt(accent.replace("#", ""), 16);
    return (num >> 16) * 0.299 + ((num >> 8) & 0xff) * 0.587 + (num & 0xff) * 0.114 > 186;
  })();
  const accentTextColor = isLight ? "#111827" : "#ffffff";

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="w-full max-w-lg">
        <Card className="shadow-lg border-0">
          <CardContent className="p-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="flex items-center gap-3 mb-6">
                <div>
                  <h2 className="text-2xl font-bold">{flow.newsletter_heading || "Subscribe to our newsletter"}</h2>
                  <p className="text-muted-foreground">
                    {flow.newsletter_subtitle || `Get ${getFrequencyText()} about ${flow.product_name}`}
                  </p>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Email address</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>

              <Button
                type="submit"
                className={`w-full font-semibold h-12 transition-opacity hover:opacity-90 ${isLight ? "border border-gray-300" : ""}`}
                style={{ backgroundColor: accent, color: accentTextColor }}
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Subscribing...
                  </>
                ) : (
                  flow.newsletter_cta_text || "Subscribe to newsletter"
                )}
              </Button>

              <p className="text-xs text-center text-gray-500">
                You'll receive {getFrequencyText()} about product updates.
              </p>
            </form>
          </CardContent>
        </Card>

        {!isPaid && <div className="mt-4 text-center">
          <p className="text-xs text-gray-500">
            Powered by <a href="https://willtheyconvert.com/" className="text-gray-600 hover:text-gray-900 underline">willtheyconvert.com</a>
          </p>
        </div>}
      </div>
    </div>
  );
} 